import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import './index.css';

import Navbar from './components/layouts/Navbar';
import UserSidebar from './components/layouts/UserSidebar';
import useSidebarToggle from './hooks/useSidebarToggle';
import UserAuthPage from './pages/public/UserAuthPage';
import ApplicationForm from './pages/user/ApplicationForm';
import ClientApplicationsPage from './pages/user/ClientApplicationsPage';
import PermitsPage from './pages/user/permitsPage';
import UserHomePage from './pages/user/UserHomePage';
import UserNotificationsPage from './pages/user/UserNotificationsPage';
import UserProfilePage from './pages/user/UserProfilePage';
// test deploy
import AboutPage from './pages/public/AboutPage';
import ContactPage from './pages/public/ContactPage';
import CreditsPage from './pages/public/CreditsPage';
import LandingPage from './pages/public/LandingPage';
import LearnMorePage from './pages/public/LearnMorePage';
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import ServicesPage from './pages/public/ServicesPage';
import TermsOfServicePage from './pages/public/TermsOfServicePage';

import PersonnelSidebar from './pages/Personnel/components/PersonnelSidebar';
import PersonnelHomePage from './pages/Personnel/PersonnelHomePage';
import PersonnelNotificationPage from './pages/Personnel/PersonnelNotificationPage';
import PersonnelProfilePage from './pages/Personnel/PersonnelProfilePage';
import PersonnelReportsPage from './pages/Personnel/PersonnelReportsPage';
import PersonnelSettingsPage from './pages/Personnel/PersonnelSettingsPage';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getUserRoles, isAuthenticated } from './utils/auth';

import SuperAdminDashboard from './pages/SuperAdmin/SuperAdminDashboard';
import SuperAdminSidebar from './pages/SuperAdmin/SuperAdminSidebar';
// import SuperAdminHomePage from './pages/SuperAdmin/SuperAdminHomePage';
import SuperAdminManageUsersPage from './pages/SuperAdmin/SuperAdminManageUsersPage';
// import SuperAdminReportsPage from './pages/SuperAdmin/SuperAdminReportsPage';
import SuperAdminSettingsPage from './pages/SuperAdmin/SuperAdminSettingsPage';

import OOPFormCreationPage from './pages/Personnel/OOPFormCreationPage';
import { checkTokenExpiration } from './utils/tokenManager';

import { ApolloProvider } from '@apollo/client';
import client from './apolloClient';

import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';

import PersonnelDashboard from './pages/Personnel/PersonnelDashboard';

import GISDashboardPage from './pages/Personnel/GISDashboardPage';
import InspectionSchedulingPage from './pages/Personnel/InspectionSchedulingPage';
import OOPPrintPage from './pages/user/OOPPrintPage';
import ORPrintPage from './pages/user/ORPrintPage';
import PaymentPage from './pages/user/PaymentPage';

import { cn } from './lib/utils';

import CSAWCertificatePrintPage from './pages/Personnel/components/CertificateComponents/CSAWCertificatePrintPage';
import CSAWTermsConditionsPrintPage from './pages/Personnel/components/CertificateComponents/CSAWTermsConditionsPrintPage';

import BC_ORPrintPage from './pages/Personnel/components/PersonnelDashboardComponents/BillCollector/BC_ORPrintPage';
import ExpiringPermitsPage from './pages/Personnel/ExpiringPermitsPage';
import FormMaintenancePage from './pages/SuperAdmin/FormMaintenancePage';
import SuperAdminActivitiesPage from './pages/SuperAdmin/SuperAdminActivitiesPage';
import InspectionsPage from './pages/user/InspectionsPage';
import VerifyCertificatePage from './pages/VerifyCertificate/VerifyCertificatePage';

import ContactDevButton from '@/components/shared/ContactDevButton';
import { Analytics } from "@vercel/analytics/react";
import TourWrapper from './components/Tour/TourWrapper';
import { TourProvider } from './contexts/TourContext';
import HelpCenterPage from './pages/HelpCenter/HelpCenterPage';

import PLTCPCertificatePrintPage from './pages/Personnel/components/CertificateComponents/PLTCPCertificatePrintPage';

const PERSONNEL_ROLES = [
   'Chief_RPS',
   'Chief_TSD',
   'Technical_Staff',
   'Forest_Ranger',
   'Receiving_Clerk',
   'Releasing_Clerk',
   'Accountant',
   'OOP_Staff_Incharge',
   'Bill_Collector',
   'Credit_Officer',
   'PENR_CENR_Officer',
   'Deputy_CENR_Officer',
   'Inspection_Team',
   'superadmin'
];

const PUBLIC_PATHS = [
   '/',
   '/auth',
   '/about',
   '/services',
   '/contact',
   '/learnMore',
   '/privacy-policy',
   '/terms',
   '/credits',
   '/verify-certificate',
   '/user/oop-print',
   '/user/or-print',
   '/personnel/or-print',
   '/personnel/csaw-certificate-print',
   '/personnel/csaw-terms-print',
   '/personnel/pltcp-certificate-print'
];

const PRINT_PATHS = [
   '/user/oop-print',
   '/user/or-print',
   '/personnel/csaw-certificate-print',
   '/personnel/pltcp-certificate-print'
];

const App = () => {
   const { sidebarToggle, toggleSidebar } = useSidebarToggle();
   const navigate = useNavigate();
   const location = useLocation();
   const [userRoles, setUserRoles] = useState([]);
   const [showNavbar, setShowNavbar] = useState(false);
   const [showSidebar, setShowSidebar] = useState(false);

   useEffect(() => {
      const authStatus = isAuthenticated();
      if (authStatus) {
         setUserRoles(getUserRoles());
      } else if (!isPublicPath(location.pathname)) {
         navigate('/auth');
      }
      // Don't show navbar for print pages
      setShowNavbar(authStatus && !isPublicPath(location.pathname));
   }, [navigate, location.pathname]);

   useEffect(() => {
      const intervalId = setInterval(() => {
         checkTokenExpiration(navigate);
      }, 60000); // Check every minute

      return () => clearInterval(intervalId);
   }, [navigate]);

   const getSidebar = () => {
      if (userRoles.includes('superadmin')) {
         return <SuperAdminSidebar isOpen={sidebarToggle} toggleSidebar={toggleSidebar} />;
      } else if (userRoles.some(role => PERSONNEL_ROLES.includes(role))) {
         return <PersonnelSidebar isOpen={sidebarToggle} onToggle={toggleSidebar} />;
      } else {
         return <UserSidebar isOpen={sidebarToggle} onToggle={toggleSidebar} />;
      }
   };

   const isPublicPath = (pathname) => {
      if (PUBLIC_PATHS.includes(pathname)) return true;
      return pathname.startsWith('/verify-certificate/') ||
         pathname === '/personnel/or-print' ||
         pathname === '/user/or-print' ||
         pathname === '/user/oop-print' ||
         pathname === '/personnel/csaw-certificate-print' ||
         pathname === '/personnel/csaw-terms-print' ||
         pathname === '/personnel/pltcp-certificate-print';
   };

   return (
      <TourProvider>
         <ApolloProvider client={client}>
            <HelmetProvider>
               <div className="relative h-screen overflow-hidden">
                  {(isAuthenticated() && !PUBLIC_PATHS.includes(location.pathname)) ? (
                     <div className="flex h-full">
                        {getSidebar()}
                        <div className={cn(
                           "flex-1 min-w-0 flex flex-col",
                           'transition-[padding] duration-300'
                        )}>
                           {showNavbar && <Navbar sidebarToggle={sidebarToggle} setSidebarToggle={toggleSidebar} />}
                           <main className={cn(
                              "flex-1 overflow-y-auto bg-background transition-colors duration-300",
                              sidebarToggle ? 'lg:pl-[260px]' : 'lg:pl-[60px]',
                              'transition-[padding] duration-300'
                           )}>
                              <Routes>
                                 <Route path="/" element={<LandingPage />} />
                                 <Route path="/auth" element={<UserAuthPage />} />
                                 <Route path="/home" element={<ProtectedRoute roles={['user']}><UserHomePage /></ProtectedRoute>} />
                                 <Route path="/permits" element={<ProtectedRoute roles={['user']}><PermitsPage /></ProtectedRoute>} />
                                 <Route path="/applicationsStatus" element={<ProtectedRoute roles={['user']}><ClientApplicationsPage /></ProtectedRoute>} />
                                 <Route path="/apply/:formType" element={<ProtectedRoute roles={['user']}><ApplicationForm /></ProtectedRoute>} />
                                 {/* <Route path="/unauthorized" element={<div className="flex items-center justify-center min-h-screen text-center">Unauthorized Access</div>} /> */}

                                 <Route path="/about" element={<AboutPage />} />
                                 <Route path="/services" element={<ServicesPage />} />
                                 <Route path="/contact" element={<ContactPage />} />
                                 <Route path="/learnMore" element={<LearnMorePage />} />
                                 <Route path="/credits" element={<CreditsPage />} />
                                 <Route path="/profile" element={<UserProfilePage />} />
                                 <Route path="/notifications" element={<ProtectedRoute roles={['user']}><UserNotificationsPage /></ProtectedRoute>} />

                                 <Route path="/personnel/home" element={<ProtectedRoute roles={PERSONNEL_ROLES}><PersonnelHomePage /></ProtectedRoute>} />
                                 <Route path="/personnel/settings" element={<ProtectedRoute roles={PERSONNEL_ROLES}><PersonnelSettingsPage /></ProtectedRoute>} />
                                 <Route path="/personnel/profile" element={<ProtectedRoute roles={PERSONNEL_ROLES}><PersonnelProfilePage /></ProtectedRoute>} />
                                 <Route path="/personnel/reports" element={<ProtectedRoute roles={PERSONNEL_ROLES}><PersonnelReportsPage /></ProtectedRoute>} />
                                 <Route path="/personnel/notifications" element={<ProtectedRoute roles={PERSONNEL_ROLES}><PersonnelNotificationPage /></ProtectedRoute>} />
                                 <Route path="/personnel/order-of-payment" element={<ProtectedRoute roles={['Chief_RPS', 'Chief_TSD', 'Accountant', 'PENR_CENR_Officer']}><OOPFormCreationPage /></ProtectedRoute>} />
                                 <Route path="/personnel/order-of-payment/:action" element={<ProtectedRoute roles={['Chief_RPS', 'Chief_TSD', 'Accountant', 'PENR_CENR_Officer']}><OOPFormCreationPage /></ProtectedRoute>} />

                                 {/* Dashboard Routes for Personnels */}
                                 <Route path="/personnel/dashboard" element={<ProtectedRoute roles={PERSONNEL_ROLES}><PersonnelDashboard /></ProtectedRoute>} />
                                 <Route path="/personnel/:role" element={<ProtectedRoute roles={PERSONNEL_ROLES}><PersonnelDashboard /></ProtectedRoute>} />

                                 {/* <Route // oop creation page only accessible to Chief_RPS, PENR_CENR_Officer, and Accountant
                                    path="/personnel/create-oop"
                                    element={
                                       <ProtectedRoute roles={['Chief_RPS', 'PENR_CENR_Officer', 'Accountant']}>
                                          <OOPFormCreationPage />
                                       </ProtectedRoute>
                                    }
                                 /> */}
                                 <Route
                                    path="/superadmin/*"
                                    element={
                                       <ProtectedRoute roles={['superadmin']}>
                                          <Routes>
                                             <Route index element={<Navigate to="dashboard" replace />} />
                                             <Route path="dashboard" element={<SuperAdminDashboard />} />
                                             <Route path="manage-users" element={<SuperAdminManageUsersPage />} />
                                             <Route path="form-maintenance" element={<FormMaintenancePage />} />
                                             <Route path="activities" element={<SuperAdminActivitiesPage />} />
                                             <Route path="settings" element={<SuperAdminSettingsPage />} />
                                          </Routes>
                                       </ProtectedRoute>
                                    }
                                 />

                                 <Route path="/user/oop-print" element={<OOPPrintPage />} />
                                 <Route path="/user/or-print" element={<ORPrintPage />} />
                                 <Route path="/personnel/or-print" element={<BC_ORPrintPage />} />
                                 <Route path="/payment/:oopId" element={<ProtectedRoute roles={['user']}><PaymentPage /></ProtectedRoute>} />
                                 <Route path="/gis" element={<ProtectedRoute roles={PERSONNEL_ROLES}><GISDashboardPage /></ProtectedRoute>} />
                                 <Route path="/personnel/inspection-scheduling" element={<ProtectedRoute roles={['Technical_Staff', 'Forest_Ranger']}><InspectionSchedulingPage /></ProtectedRoute>} />
                                 <Route path="/inspections" element={<ProtectedRoute roles={['user']}><InspectionsPage /></ProtectedRoute>} />
                                 <Route path="/verify-certificate/:id" element={<VerifyCertificatePage />} />
                                 <Route path="/personnel/csaw-certificate-print" element={<ProtectedRoute roles={['Technical_Staff']}><CSAWCertificatePrintPage /></ProtectedRoute>} />
                                 <Route path="/personnel/csaw-terms-print" element={<CSAWTermsConditionsPrintPage />} />
                                 <Route
                                    path="/personnel/expiring-permits"
                                    element={
                                       <ProtectedRoute roles={['Technical_Staff', 'Releasing_Clerk', 'PENR_CENR_Officer']}>
                                          <ExpiringPermitsPage />
                                       </ProtectedRoute>
                                    }
                                 />
                                 <Route
                                    path="/help"
                                    element={
                                       <ProtectedRoute roles={['user']}>
                                          <HelpCenterPage />
                                       </ProtectedRoute>
                                    }
                                 />
                                 <Route
                                    path="/help-center"
                                    element={
                                       <ProtectedRoute roles={['user', 'superadmin', ...PERSONNEL_ROLES]}>
                                          <HelpCenterPage />
                                       </ProtectedRoute>
                                    }
                                 />
                                 <Route path="/personnel/pltcp-certificate-print" element={<ProtectedRoute roles={['Technical_Staff']}><PLTCPCertificatePrintPage /></ProtectedRoute>} />
                              </Routes>
                           </main>
                        </div>
                     </div>
                  ) : (
                     <main className={cn(
                        "h-full overflow-y-auto bg-background",
                        'p-0'
                     )}>
                        <Routes>
                           <Route path="/" element={<LandingPage />} />
                           <Route path="/auth" element={<UserAuthPage />} />
                           <Route path="/about" element={<AboutPage />} />
                           <Route path="/services" element={<ServicesPage />} />
                           <Route path="/contact" element={<ContactPage />} />
                           <Route path="/learnMore" element={<LearnMorePage />} />
                           <Route path="/credits" element={<CreditsPage />} />
                           <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                           <Route path="/terms" element={<TermsOfServicePage />} />
                           <Route path="/user/oop-print" element={<OOPPrintPage />} />
                           <Route path="/user/or-print" element={<ORPrintPage />} />
                           <Route path="/personnel/or-print" element={<BC_ORPrintPage />} />
                           <Route path="/personnel/csaw-terms-print" element={<CSAWTermsConditionsPrintPage />} />
                           <Route path="/personnel/csaw-certificate-print" element={<CSAWCertificatePrintPage />} />
                           <Route path="/personnel/pltcp-certificate-print" element={<PLTCPCertificatePrintPage />} />
                           <Route path="/verify-certificate/:id" element={<VerifyCertificatePage />} />
                        </Routes>
                     </main>
                  )}
                  <ContactDevButton />
                  <ToastContainer />
                  <Toaster position="top-right" closeButton richColors duration={3000} />
                  <TourWrapper />
                  <Analytics />
               </div>
            </HelmetProvider>
         </ApolloProvider>
      </TourProvider>
   );
};

export default App;
